/**
 * 받는사람 · 참조 · 숨은참조를 줄로 나눠 보여준다 (2026-08-22).
 *
 * 여태 상세는 세 종류를 **한 줄에 이어 붙여** "받는 사람:" 이라고 적었다
 * (MailDetail.tsx 의 recipientLabels). 그래서 참조로 받은 사람이 받는사람처럼 보였고,
 * 사람이 많으면 한 줄에서 잘려 누가 더 있는지 알 방법이 없었다.
 *
 * 서버는 이미 종류를 준다 — `mail_recipients.type` = TO | CC | BCC (VarChar(5), 기본 TO).
 * 화면이 그걸 안 쓰고 있었을 뿐이라, **서버 변경 없이** 나눌 수 있다.
 *
 * ★ 숨은참조는 서버가 **보낸 사람에게만** 내려준다(mail-helpers 의 filterVisibleRecipientsByBcc).
 *   여기서 다시 거르지 않는다 — 걸러 버리면 정작 보낸 사람이 자기가 숨은참조로 넣은 사람을
 *   못 보게 된다.
 * ★ 모르는 종류가 와도 **사라지지 않는다** — 받는사람 묶음으로 떨어진다. 구 서버는 전부 'TO' 다.
 */

import { useState, type ReactNode } from 'react';
import type { MailRecipientDetail } from '../../api/mail';
import { tMail } from '../../lib/mail-i18n';

interface MailAddressRowsProps {
  recipients: MailRecipientDetail[];
  /** 내 사용자 id — 목록에서 나를 '(나)' 로 표시한다. 없으면 표시만 생략한다. */
  myUserId: string | null;
  /**
   * 줄 «끝»에 붙는 것 (2026-09-08 사용자 지적) — 「주소록 담기」.
   *
   * ★ 담기는 **그 줄의 사람들**을 대상으로 한다 — 받는사람 줄의 단추는 받는사람만,
   *   참조 줄의 단추는 참조만. 「이 메일 전부」로 뭉뚱그리면 **어느 줄을 담는지**가 사라진다.
   * ★ 선택이다. 안 주면 지금까지처럼 줄 끝에 아무것도 없다.
   */
  renderGroupAction?: (key: 'TO' | 'CC' | 'BCC', list: MailRecipientDetail[]) => ReactNode;
}

/** 이 수만 넘으면 접는다. 좁은 상세에서도 한 줄에 들어가는 크기. */
const FOLD_AT = 3;

type GroupKey = 'TO' | 'CC' | 'BCC';

const GROUP_ORDER: Array<{ key: GroupKey; labelKey: 'mail.field.to' | 'mail.field.cc' | 'mail.field.bcc' }> = [
  { key: 'TO', labelKey: 'mail.field.to' },
  { key: 'CC', labelKey: 'mail.field.cc' },
  { key: 'BCC', labelKey: 'mail.field.bcc' },
];

/** 모르는 값은 받는사람으로 본다 — 어떤 종류든 화면에서 없어지면 안 된다. */
function groupOf(type: string): GroupKey {
  const upper = (type || '').toUpperCase();
  if (upper === 'CC' || upper === 'BCC') return upper;
  return 'TO';
}

/** 이름이 비어 있으면 주소로 대신한다 — 밖에서 온 주소는 이름이 없을 수 있다. */
function displayName(r: MailRecipientDetail, isMe: boolean): string {
  const base = r.name?.trim() || r.email;
  return isMe ? `${base} ${tMail('mail.detail.addr_me')}` : base;
}

export function MailAddressRows({ recipients, myUserId, renderGroupAction }: MailAddressRowsProps) {
  // 묶음마다 따로 펼친다 — 참조를 펼쳤다고 받는사람까지 펼쳐질 이유가 없다.
  const [opened, setOpened] = useState<GroupKey[]>([]);

  if (recipients.length === 0) return null;

  const groups: Record<GroupKey, MailRecipientDetail[]> = { TO: [], CC: [], BCC: [] };
  for (const r of recipients) groups[groupOf(r.type)].push(r);

  return (
    <div className="mail-detail-addrs">
      {GROUP_ORDER.map(({ key, labelKey }) => {
        const list = groups[key];
        if (list.length === 0) return null;

        const isOpen = opened.includes(key);
        const folded = !isOpen && list.length > FOLD_AT;
        const shown = folded ? list.slice(0, FOLD_AT) : list;
        const hidden = list.length - shown.length;
        // 제목(title) 에는 언제나 전원을 넣는다 — 접혀 있어도 마우스를 올리면 다 보인다.
        const full = list.map((r) => `${r.name || r.email} <${r.email}>`).join(', ');

        return (
          <div className="mail-detail-addr-row" key={key}>
            <span className="mail-detail-addr-k">{tMail(labelKey)}</span>
            <span className="mail-detail-addr-v" title={full}>
              {shown.map((r, i) => {
                const isMe = myUserId != null && r.userId === myUserId;
                return (
                  <span key={r.id} className={`mail-detail-addr-one${isMe ? ' mail-detail-addr-one--me' : ''}`}>
                    {displayName(r, isMe)}
                    {i < shown.length - 1 ? ', ' : ''}
                  </span>
                );
              })}
            </span>
            {renderGroupAction?.(key, list)}
            {list.length > FOLD_AT && (
              <button
                type="button"
                className="mail-detail-addr-more"
                onClick={() => setOpened((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))}
                aria-expanded={isOpen}
              >
                {folded
                  ? tMail('mail.detail.addr_more').replace('{n}', String(hidden))
                  : tMail('mail.detail.addr_less')}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
